import React, { useEffect, useMemo, useState } from "react";

import {
  Folder,
  Image,
  Film,
  FileText,
  File,
  Search,
  Grid,
  List,
  RefreshCcw,
} from "lucide-react";
import useGallery from "../../services/Order/useGallery";
import LoadingScreen from "../../utils/LoadingScreen";

const prettyName = (str = "") => str.replaceAll("_", " ");

const typeIcon = (type) => {
  if (type === "IMAGE") return <Image size={28} />;
  if (type === "VIDEO") return <Film size={28} />;
  if (type === "DOCUMENT") return <FileText size={28} />;
  return <File size={28} />;
};

export default function Gallery() {
  const {
    galleryItems = [],
    loading,
    error,
    folderStack = [],
    openFolder,
    goBack,
    goToCrumb,
    getMediaGallery,
    currentDir,
  } = useGallery();

  const [view, setView] = useState("grid");
  const [query, setQuery] = useState("");

  useEffect(() => {
    getMediaGallery(currentDir);
  }, []);

  const folders = useMemo(
    () => galleryItems.filter((i) => i.fileType === "FILE"),
    [galleryItems],
  );

  const files = useMemo(
    () => galleryItems.filter((i) => i.fileType !== "FILE"),
    [galleryItems],
  );

  const filteredFiles = files.filter((f) =>
    f.fileName.toLowerCase().includes(query.toLowerCase()),
  );

  const filteredFolders = folders.filter((f) =>
    f.fileName.toLowerCase().includes(query.toLowerCase()),
  );

  return (
    <div className="p-6 space-y-6 min-h-0 h-full overflow-hidden flex flex-col montserrat-font-1">
      {/* HEADER */}
      <div className="flex flex-col gap-4 montserrat-font-1  md:flex-row md:items-center md:justify-between bg-white p-8 rounded-lg">
        <h1 className="text-2xl font-semibold">Gallery</h1>

        <div className="flex items-center gap-3">
          <div className="flex items-center border rounded-lg px-3 py-1">
            <Search size={16} className="mr-2 text-gray-400" />
            <input
              className="outline-none text-sm"
              placeholder="Search..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <button
            onClick={() => setView("grid")}
            className={`p-2 rounded ${view === "grid" ? "bg-green-600 text-white" : "border"}`}
          >
            <Grid size={16} />
          </button>

          <button
            onClick={() => setView("list")}
            className={`p-2 rounded ${view === "list" ? "bg-green-600 text-white" : "border"}`}
          >
            <List size={16} />
          </button>

          <button
            onClick={() => getMediaGallery(currentDir)}
            className="border px-3 py-1 flex items-center gap-2 bg-green-600 rounded-lg text-white"
          >
            <RefreshCcw size={14} /> Refresh
          </button>
        </div>
      </div>

      {/* BREADCRUMB */}
    

      {folderStack.length > 1 && (
        <button onClick={goBack} className="text-sm text-blue-500">
          ← Back
        </button>
      )}

      {/* LOADING */}
      {loading && <div className="text-center text-gray-500"><LoadingScreen/></div>}

      {/* ERROR */}
      {error && <div className="text-red-500">{error}</div>}

      {/* CONTENT */}

      <div
        className={
          view === "grid"
            ? "grid grid-cols-2 overflow-auto h-full md:grid-cols-4 lg:grid-cols-6 gap-4 bg-white p-4 rounded-lg"
            : "flex flex-col gap-2"
        }
      >
        {/* FOLDERS */}

        {filteredFolders.map((folder) => (
          <button
            key={folder.galleryId}
            onClick={() => openFolder(folder.fileName)}
            className="border rounded-xl p-4 flex flex-col over items-center hover:shadow"
          >
            <Folder size={40} className="text-green-400 " />
            <span className="text-sm mt-2 text-center">
              {prettyName(folder.fileName)}
            </span>
          </button>
        ))}

        {/* FILES */}

        {filteredFiles.map((file) => (
          <div
            key={file.galleryId}
            className="border rounded-xl p-4 flex flex-col items-center hover:shadow"
          >
            {file.fileType === "IMAGE" ? (
              <img
                src={file.fileUrl}
                className="w-full h-28 object-cover rounded"
              />
            ) : (
              typeIcon(file.fileType)
            )}

            <span className="text-xs mt-2 text-center">
              {prettyName(file.fileName)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
